/**
 * Shared helpers for Multi-Territory advance-search fields.
 *
 * Date columns in MT advance search (Created date, Updated date, Start date,
 * End date, etc.) must:
 *   - render a real date picker (native `type="date"` handled by
 *     `components/common/Search.jsx`),
 *   - reject free-typed alphabets / special characters,
 *   - use a short placeholder that is not truncated.
 *
 * The native date input yields an ISO `yyyy-mm-dd` value; the WHT backend
 * search expects `dd/mm/yyyy` (same as the India TAN Master page). Use
 * `formatMTSearchCriteria` in the screen's onApply handler to convert date
 * values before calling the list/search API.
 */

/**
 * Words that must stay fully UPPERCASE in MT headers / advance-search labels
 * (acronyms / IDs). Matched case-insensitively per word. Keep this in sync with
 * the HEADER_ACRONYMS set that `MTCommonTable` consumes — they share this one
 * source of truth so the advance-search field label and the table header label
 * are always rendered identically.
 */
export const MT_HEADER_ACRONYMS = new Set([
  'ID',
  'TIN',
  'TAN',
  'PE',
  'PAN',
  'GST',
  'GSTIN',
  'NRIC',
  'TRN',
  'UUID',
  'UEN',
  'DOB',
  'URL',
  'DTAA',
  'WHT',
  'FU',
  'MIS',
  'STD',
])

/**
 * Format a column / search label as sentence case ("running text"), e.g.
 *   "Entity Tax Identification Number" -> "Entity tax identification number"
 *   "Rule Name"                        -> "Rule name"
 *   "Mapped Nature of Service"         -> "Mapped nature of service"
 *   "entity tax identification number" -> "Entity tax identification number"
 *   "Payee id type"                    -> "Payee ID type"
 *
 * Rules (identical to the MT table header formatter so a search field label and
 * its table header always match exactly):
 * - First word: capitalize the first letter, lowercase the rest.
 * - Other words: lowercase.
 * - Any word in MT_HEADER_ACRONYMS is forced UPPERCASE regardless of position
 *   (so "ID", "TIN", "PAN"… stay in capital letters).
 * - Field-key style tokens (containing an underscore or a digit, e.g. "FU_1")
 *   are left untouched.
 */
export const formatMTHeaderLabel = (label: string): string => {
  if (!label) return label
  const words = label.trim().split(/\s+/).filter(Boolean)
  return words
    .map((word, index) => {
      if (word.includes('_') || /\d/.test(word)) return word
      const upper = word.toUpperCase()
      if (MT_HEADER_ACRONYMS.has(upper)) return upper
      const lower = word.toLowerCase()
      if (index === 0) return lower.charAt(0).toUpperCase() + lower.slice(1)
      return lower
    })
    .join(' ')
}

/** Field ids that represent a date in MT list/search configs. */
export const MT_DATE_FIELD_IDS = [
  'createdDate',
  'lastModifiedDate',
  'updatedDate',
  'startDate',
  'endDate',
  'createdAt',
  'updatedAt',
  // Upload page — "Uploaded date" column.
  'startedAt',
  // snake_case variants (config-driven grids expose columns as snake_case ids,
  // e.g. the Returns "Update Date" column -> update_date / updated_date).
  'created_date',
  'last_modified_date',
  'updated_date',
  'update_date',
  'start_date',
  'end_date',
  'created_at',
  'updated_at',
  'started_at',
] as const

/**
 * `displayType` values (from config-driven grid/column metadata) that mean the
 * column holds a date and should therefore render a date picker in advance
 * search (e.g. the Returns grid "Update Date" column uses `DATETIME`).
 */
export const MT_DATE_DISPLAY_TYPES = [
  'DATE',
  'DATETIME',
  'DATE_PICKER',
] as const

/** Placeholder shown for MT date search fields (short, avoids truncation). */
export const MT_DATE_PLACEHOLDER = 'DD/MM/YYYY'

/** Returns true when a column `displayType`/`type` denotes a date column. */
export const isMTDateDisplayType = (displayType?: string): boolean => {
  if (!displayType) return false
  return (MT_DATE_DISPLAY_TYPES as readonly string[]).includes(
    displayType.toUpperCase()
  )
}

/**
 * Returns true when a column id represents a date field. Matching is
 * case-insensitive and also catches ids that simply end in "date"/"at"
 * date-like suffixes (e.g. `uploadDate`, `uploadAt`, `update_date`).
 */
export const isMTDateField = (id: string): boolean => {
  if (!id) return false
  const normalized = id.toLowerCase()
  if (
    (MT_DATE_FIELD_IDS as readonly string[]).some(
      known => known.toLowerCase() === normalized
    )
  ) {
    return true
  }
  // Generic suffix match so new date columns work without an explicit entry.
  return /(date|_at)$/.test(normalized)
}

export interface MTSearchColumn {
  id: string
  label: string
  type?: string
  placeholder?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export interface MTSearchField {
  id: string
  label: string
  type: string
  placeholder: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

/**
 * Builds an advance-search field descriptor from a column definition.
 * Date columns become a native date picker (`type: 'date'`) with a short
 * placeholder; everything else falls back to a plain text field.
 */
export const buildMTSearchField = (col: MTSearchColumn): MTSearchField => {
  // Render the advance-search field label exactly like the table header
  // (sentence case / "running text", acronyms such as ID/TIN kept uppercase).
  const label = formatMTHeaderLabel(col.label)
  const displayType = (col.displayType ?? col.type) as string | undefined
  if (isMTDateField(col.id) || isMTDateDisplayType(displayType)) {
    return {
      ...col,
      id: col.id,
      label,
      type: 'date',
      placeholder: MT_DATE_PLACEHOLDER,
    }
  }
  return {
    ...col,
    id: col.id,
    label,
    type: col.type || 'text',
    placeholder: col.placeholder || `Enter ${label}`,
  }
}

/** Converts an ISO `yyyy-mm-dd` string to `dd/mm/yyyy`. Leaves other values untouched. */
export const isoToDdMmYyyy = (value: unknown): unknown => {
  if (typeof value !== 'string') return value
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
  if (!match) return value
  const [, yyyy, mm, dd] = match
  return `${dd}/${mm}/${yyyy}`
}

/**
 * Normalises search criteria coming out of the advance-search popover so that
 * date field values (emitted as ISO `yyyy-mm-dd` by the native date picker)
 * are sent to the backend as `dd/mm/yyyy`.
 */
export const formatMTSearchCriteria = <T extends Record<string, unknown>>(
  criteria: T[]
): T[] =>
  criteria.map(item => {
    const fieldId = (item.fieldName ?? item.field) as string | undefined
    if (fieldId && isMTDateField(fieldId)) {
      if ('value' in item && item.value != null) {
        return { ...item, value: isoToDdMmYyyy(item.value) }
      }
      if (Array.isArray((item as Record<string, unknown>).searchValueList)) {
        const list = (item as Record<string, unknown>)
          .searchValueList as unknown[]
        return {
          ...item,
          searchValueList: list.map(isoToDdMmYyyy),
        }
      }
    }
    return item
  })
